import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { Button } from "@/components/flexkit/Button";
import { toast } from "@/components/podkit/toasts/use-toast";
import { useFlagPrebuildsEnabled } from "@/hooks/use-flag";
import { formatError } from "@/utils/errors";
import { type FC, useCallback, useState } from "react";
import { useCreatePrebuild } from "@/queries/prebuild-queries";

type TriggerPrebuildButtonProps = {
    projectId: string;
    environmentClassIds: string[];
};

export const TriggerPrebuildButton: FC<TriggerPrebuildButtonProps> = ({ projectId, environmentClassIds }) => {
    const { value: isPrebuildsEnabled } = useFlagPrebuildsEnabled();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const createPrebuild = useCreatePrebuild();

    const onClick = useCallback(async () => {
        if (environmentClassIds.length === 0) {
            toast({
                title: "No prebuild environment classes configured",
                description: "Please configure environment classes for this project to trigger prebuilds",
            });
            return;
        }

        try {
            setIsSubmitting(true);
            for (const classId of environmentClassIds) {
                await createPrebuild.mutateAsync({ projectId, environmentClassId: classId });
            }
            let message = "prebuild";
            if (environmentClassIds.length > 1) {
                message += "s";
            }
            toast({ title: message + " triggered" });
        } catch (error: unknown) {
            toast({ title: "Could not trigger prebuild", description: formatError(error) });
        } finally {
            setIsSubmitting(false);
        }
    }, [projectId, environmentClassIds, createPrebuild]);

    return (
        <Button
            variant="brand"
            size="sm"
            LeadingIcon={IconPlay}
            onClick={onClick}
            loading={isSubmitting}
            disabled={!isPrebuildsEnabled}
            title={!isPrebuildsEnabled ? "Prebuilds are not enabled for this org" : undefined}
            data-testid="trigger-prebuild-button"
            data-tracking-id="trigger-prebuild-button"
        >
            Prebuild
        </Button>
    );
};
